import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiTrendingDown } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import type { ChurnItem } from '../types';

interface ChurnDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

const formatDuration = (days: number): string => {
  if (days < 30) return `${days} dia${days !== 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} ano${years !== 1 ? 's' : ''}`;
  return `${years} ano${years !== 1 ? 's' : ''} e ${remainingMonths} ${remainingMonths === 1 ? 'mês' : 'meses'}`;
};

export const ChurnDrawer: React.FC<ChurnDrawerProps> = ({ isOpen, onClose }) => {
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-dashboard-churn'],
    queryFn: () => apiGet<ChurnItem[]>('/dashboard/churn-list'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const lostMrr = items?.reduce((sum, i) => sum + i.monthlyValueAtCancellation, 0) || 0;
  const subtitle = items
    ? `${items.length} cancelamento${items.length !== 1 ? 's' : ''} · MRR perdido ${formatCurrency(lostMrr)}`
    : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiTrendingDown className="w-5 h-5" />}
      title="Empresas Canceladas (Churn)"
      subtitle={subtitle}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={84} variant="rectangular" />
          ))}
        </div>
      ) : items && items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.companyId}
              className="p-3 rounded-lg space-y-2"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {item.companyName}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                    {item.planName}
                  </p>
                </div>
                <p className="text-sm font-semibold tabular-nums flex-shrink-0" style={{ color: '#ef4444' }}>
                  -{formatCurrency(item.monthlyValueAtCancellation)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {item.subscribedAt && <span>Assinou em <strong>{formatDate(item.subscribedAt)}</strong></span>}
                <span>Cancelou em <strong>{formatDate(item.cancelledAt)}</strong></span>
                <span>Ficou ativo por <strong>{formatDuration(item.activeDurationDays)}</strong></span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiTrendingDown className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum cancelamento
          </p>
        </div>
      )}
    </Drawer>
  );
};
